import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Copy, RefreshCw, X, Loader2, ExternalLink, ScanSearch, CheckCircle2, AlertCircle, Languages } from "lucide-react";
import { BridgeDiagnosticPanel } from "@/components/BridgeDiagnosticPanel";

export const FIXABLE_CATEGORIES = ["Meta description", "Titre SEO", "H1", "Canonical", "Open Graph", "Schema"];

export function validateSchemaJson(text) {
  const t = (text || "").trim();
  if (!t) return { valid: false, error: "JSON-LD vide." };
  let obj;
  try { obj = JSON.parse(t); } catch (e) { return { valid: false, error: `JSON non parseable : ${e.message}` }; }
  if (obj === null || typeof obj !== "object" || Array.isArray(obj)) return { valid: false, error: "Le JSON-LD doit être un objet unique { … }." };
  if (!obj["@type"]) return { valid: false, error: "Champ obligatoire manquant : @type." };
  if (!obj["@context"]) return { valid: false, error: "Champ obligatoire manquant : @context (https://schema.org)." };
  return { valid: true, error: null, type: String(obj["@type"]) };
}

export function FixButton({ issue, siteId, onOpen, onAltOpen, onHreflangOpen }) {
  if (issue.category === "Hreflang" && onHreflangOpen) {
    return (
      <button
        onClick={() => onHreflangOpen(issue)}
        data-testid="hreflang-guide-button"
        className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 border border-indigo-300 rounded-md px-2 py-1 hover:bg-indigo-50 transition-colors"
      >
        <Languages className="w-3 h-3" /> Guide hreflang
      </button>
    );
  }
  if (issue.category === "Images" && onAltOpen) {
    return (
      <button
        onClick={() => onAltOpen(issue)}
        data-testid="audit-alt-diagnostic-button"
        className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 border border-indigo-300 rounded-md px-2 py-1 hover:bg-indigo-50 transition-colors"
      >
        <ScanSearch className="w-3 h-3" /> Diagnostic ALT
      </button>
    );
  }
  // issue.type = diagnostic Phase D (indexation) : jamais de bouton de correction automatique
  if (!issue.type && FIXABLE_CATEGORIES.includes(issue.category)) {
    return (
      <button
        onClick={() => onOpen(issue)}
        data-testid="audit-fix-button"
        className="inline-flex items-center gap-1 text-xs font-medium text-[#002FA7] border border-[#002FA7]/30 rounded-md px-2 py-1 hover:bg-blue-50 transition-colors"
      >
        <Sparkles className="w-3 h-3" /> Corriger
      </button>
    );
  }
  return (
    <a href={issue.page_url} target="_blank" rel="noreferrer" data-testid="audit-view-page-link"
      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
      <ExternalLink className="w-3 h-3" /> Voir la page
    </a>
  );
}

function SocialPreviewCard({ label, title, description, image, imageOk, pageUrl, testid }) {
  let domain = "";
  try { domain = new URL(pageUrl).host; } catch { /* noop */ }
  const showImage = image && imageOk !== false;
  return (
    <div data-testid={testid} className="border border-slate-200 rounded-md overflow-hidden bg-white">
      <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-50 border-b border-slate-100">{label}</div>
      {showImage ? (
        <img src={image} alt="" className="w-full h-28 object-cover" onError={(e) => { e.target.style.display = "none"; }} />
      ) : (
        <div className="w-full h-28 bg-slate-100 flex items-center justify-center text-xs text-slate-500">Image d'aperçu manquante</div>
      )}
      <div className="p-2">
        <div className="text-[10px] text-slate-400 uppercase">{domain}</div>
        <div className="text-sm font-semibold text-slate-900 leading-tight line-clamp-2">
          {title || <em className="text-slate-400 font-normal">Titre manquant</em>}
        </div>
        <div className="text-xs text-slate-500 line-clamp-2">{description || ""}</div>
      </div>
    </div>
  );
}

export function FixDrawer({ issue, siteId, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const isOG = issue.category === "Open Graph";
  const [suggestion, setSuggestion] = useState("");
  const [og, setOg] = useState({ ogTitle: "", ogDescription: "", ogImage: "" });
  const [ogRaw, setOgRaw] = useState(false);
  const [before, setBefore] = useState(null);
  const [beforeImgOk, setBeforeImgOk] = useState(null);
  const [imgCheck, setImgCheck] = useState(null);
  const [keep, setKeep] = useState(null);
  const [source, setSource] = useState(null);
  const [pageLang, setPageLang] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [verifyState, setVerifyState] = useState(null);

  const checkImage = async (url) => {
    if (!url || !url.trim()) { setImgCheck(null); return; }
    try {
      const { data } = await api.post("/seo-fix/og-preview", { site_id: siteId, image_url: url.trim() });
      setImgCheck(data.image_check || null);
    } catch { setImgCheck(null); }
  };

  const generate = async () => {
    setLoading(true);
    setKeep(null);
    try {
      const { data } = await api.post("/seo-fix/suggest", {
        site_id: siteId, page_url: issue.page_url, category: issue.category,
        message: issue.message, current_value: issue.current_value || null,
      });
      setSource(data.source);
      setPageLang(data.page_lang || null);
      if (data.keep) {
        setKeep(data.keep_reason || "Valeur actuelle déjà pertinente");
        setSuggestion("");
        return;
      }
      if (isOG) {
        try {
          const parsed = JSON.parse(data.suggestion);
          const next = { ogTitle: parsed.ogTitle || "", ogDescription: parsed.ogDescription || "", ogImage: parsed.ogImage || "" };
          setOg(next);
          setOgRaw(false);
          if (next.ogImage) checkImage(next.ogImage);
        } catch {
          setOgRaw(true);
          setSuggestion(data.suggestion || "");
        }
      } else {
        setSuggestion(data.suggestion || "");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la génération IA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generate();
    if (isOG) {
      api.post("/seo-fix/og-preview", { site_id: siteId, page_url: issue.page_url })
        .then(({ data }) => {
          setBefore(data.current || null);
          setBeforeImgOk(data.current_image_check ? !!data.current_image_check.accessible : null);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveNewValue = () => {
    if (!isOG || ogRaw) return suggestion;
    const clean = {};
    if (og.ogTitle.trim()) clean.ogTitle = og.ogTitle.trim();
    if (og.ogDescription.trim()) clean.ogDescription = og.ogDescription.trim();
    if (og.ogImage.trim()) clean.ogImage = og.ogImage.trim();
    return Object.keys(clean).length ? JSON.stringify(clean) : "";
  };

  const apply = async (force = false) => {
    setApplying(true);
    try {
      const { data } = await api.post("/seo-fix/apply", {
        site_id: siteId, page_url: issue.page_url, category: issue.category,
        old_value: issue.current_value || null, new_value: effectiveNewValue(), force,
      });
      setResult(data);
      if (data.status === "published") toast.success("Correction appliquée ✓ — vérification automatique lancée");
      else if (data.status === "velo_pushed") toast.success("Enregistré via le pont Velo ✓ — vérification publique lancée");
      else if (data.status === "conflict") toast.warning("La page a changé depuis l'audit — vérifiez la valeur actuelle.");
      else if (data.status === "rejected") toast.error(data.note);
      else toast.warning(data.note);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'application");
    } finally {
      setApplying(false);
    }
  };

  // Vérification automatique après publication / push Velo (poll toutes les 25s, max 6 essais)
  useEffect(() => {
    if (!["published", "velo_pushed"].includes(result?.status || "") || !result?.fix_id) return;
    let cancelled = false;
    let attempt = 0;
    const max = 6;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      attempt += 1;
      setVerifyState({ attempt, max, gaveUp: false });
      try {
        const { data } = await api.post(`/seo-fix/verify/${result.fix_id}`);
        if (data.verified && !cancelled) {
          setResult((r) => ({ ...r, status: "verified" }));
          setVerifyState(null);
          toast.success("Vérifié ✓ — la correction est visible publiquement");
          return;
        }
      } catch { /* retry */ }
      if (cancelled) return;
      if (attempt < max) timer = setTimeout(tick, 25000);
      else setVerifyState({ attempt, max, gaveUp: true });
    };
    timer = setTimeout(tick, 15000);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.status, result?.fix_id]);

  const verifyNow = async () => {
    try {
      const { data } = await api.post(`/seo-fix/verify/${result.fix_id}`);
      if (data.verified) {
        setResult({ ...result, status: "verified" });
        setVerifyState(null);
        toast.success("Vérifié ✓ — visible publiquement");
        if (data.reaudit_started) toast.info("Re-audit automatique lancé — score mis à jour dans l'onglet Corrections");
      }
      else toast.warning(data.note || "Pas encore visible — réessayez dans 2-3 min");
    } catch (e) { toast.error(e?.response?.data?.detail || "Vérification impossible"); }
  };

  const isSchema = issue.category === "Schema";
  const schemaCheck = isSchema && !loading && !keep ? validateSchemaJson(suggestion) : null;

  const canApply = isOG && !ogRaw
    ? (og.ogTitle.trim() || og.ogDescription.trim() || og.ogImage.trim())
    : isSchema
      ? !!suggestion && schemaCheck?.valid
      : !!suggestion;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="fix-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold text-slate-950 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#002FA7]" /> Corriger avec l'IA
          </h2>
          <button onClick={onClose} data-testid="fix-drawer-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-sm font-medium text-slate-900">{issue.page_title}</div>
        <a href={issue.page_url} target="_blank" rel="noreferrer" className="text-xs text-[#002FA7] hover:underline break-all">{issue.page_url}</a>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs font-semibold text-red-600 uppercase tracking-wider">{issue.category}</span>
          {pageLang && (
            <span data-testid="fix-page-lang-badge"
              title="Langue détectée de la page — la suggestion IA est rédigée dans cette langue"
              className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 text-slate-700 uppercase">
              {pageLang}
            </span>
          )}
        </div>
        <div className="text-sm text-slate-700">{issue.message}</div>

        {keep && (
          <div className="mt-4 text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-800" data-testid="fix-keep-existing">
            ✓ Analysé — aucune modification nécessaire (aucune écriture). <span className="italic">{keep}</span>
          </div>
        )}

        {!isOG && (
          <div className="mt-4">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Avant (valeur actuelle)</div>
            <div className="border border-slate-200 bg-slate-50 rounded-md px-3 py-2 text-sm text-slate-600" data-testid="fix-before-value">
              {issue.current_value || <em className="text-slate-400">Aucune valeur</em>}
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-4 border border-slate-200 rounded-md px-3 py-4 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Génération en cours…
          </div>
        ) : isOG && !ogRaw && !keep ? (
          <div className="mt-4 space-y-3" data-testid="fix-og-editor">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">og:title</label>
                <span className={`text-[11px] ${og.ogTitle.length > 70 ? "text-amber-600" : "text-slate-400"}`}>{og.ogTitle.length}/70</span>
              </div>
              <input value={og.ogTitle} onChange={(e) => setOg({ ...og, ogTitle: e.target.value })}
                data-testid="fix-og-title-input"
                className="w-full border border-emerald-300 bg-emerald-50/40 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">og:description</label>
                <span className={`text-[11px] ${og.ogDescription.length > 200 ? "text-amber-600" : "text-slate-400"}`}>{og.ogDescription.length}/200</span>
              </div>
              <textarea value={og.ogDescription} rows={2} onChange={(e) => setOg({ ...og, ogDescription: e.target.value })}
                data-testid="fix-og-description-input"
                className="w-full border border-emerald-300 bg-emerald-50/40 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">og:image (URL HTTPS publique — modifiable)</label>
              <input value={og.ogImage}
                onChange={(e) => { setOg({ ...og, ogImage: e.target.value }); setImgCheck(null); }}
                onBlur={(e) => checkImage(e.target.value)}
                placeholder="https://… (image réellement existante)"
                data-testid="fix-og-image-input"
                className="w-full border border-emerald-300 bg-emerald-50/40 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40" />
              {og.ogImage.trim() && imgCheck && (
                <div className={`mt-1 inline-flex items-center gap-1 text-xs ${imgCheck.accessible ? "text-green-700" : "text-red-600"}`} data-testid="fix-og-image-check">
                  {imgCheck.accessible ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                  {imgCheck.accessible ? "Image accessible ✓" : `Image inaccessible — ${imgCheck.reason || "vérifiez l'URL"}`}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <SocialPreviewCard label="Avant (balises publiques actuelles)" testid="fix-og-preview-before"
                title={before?.["og:title"]} description={before?.["og:description"]}
                image={before?.["og:image"]} imageOk={beforeImgOk} pageUrl={issue.page_url} />
              <SocialPreviewCard label="Après (proposition)" testid="fix-og-preview-after"
                title={og.ogTitle} description={og.ogDescription}
                image={og.ogImage.trim()} imageOk={imgCheck ? imgCheck.accessible : null} pageUrl={issue.page_url} />
            </div>
            <p className="text-[11px] text-slate-500">
              Aperçu indicatif (style carte sociale) — LinkedIn peut conserver son propre cache après publication.
              Le statut « Vérifié ✓ » vient uniquement de la relecture des vraies balises du HTML public.
            </p>
            <div className="flex items-center gap-3">
              <button onClick={generate} disabled={loading} data-testid="fix-regenerate-button"
                className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline">
                <RefreshCw className="w-3 h-3" /> Régénérer
              </button>
              <button onClick={() => { navigator.clipboard.writeText(effectiveNewValue()); toast.success("Copié !"); }}
                data-testid="fix-copy-button" className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
                <Copy className="w-3 h-3" /> Copier
              </button>
              <button onClick={() => { toast.info("Valeur existante conservée — aucune écriture effectuée."); onClose(); }}
                data-testid="fix-keep-button" className="text-xs text-slate-600 hover:underline">
                Conserver l'existant
              </button>
            </div>
          </div>
        ) : !keep ? (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">Après (suggestion IA — modifiable)</div>
              <span className="text-[11px] text-slate-400">{suggestion.length} caractères</span>
            </div>
            <textarea value={suggestion} onChange={(e) => setSuggestion(e.target.value)} rows={issue.category === "Schema" ? 8 : 4}
              data-testid="fix-suggestion-textarea"
              className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 ${isSchema && schemaCheck && !schemaCheck.valid ? "border-red-300 bg-red-50/40 focus:ring-red-400/40" : "border-emerald-300 bg-emerald-50/40 focus:ring-emerald-400/40"}`} />
            {isSchema && schemaCheck && (
              schemaCheck.valid ? (
                <div className="mt-1 inline-flex items-center gap-1 text-xs text-green-700" data-testid="fix-schema-valid">
                  <CheckCircle2 className="w-3.5 h-3.5" /> JSON-LD valide ✓ (@type : {schemaCheck.type}) — reparsé automatiquement à chaque modification
                </div>
              ) : (
                <div className="mt-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="fix-schema-error">
                  <div className="font-semibold flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> JSON-LD invalide — application désactivée</div>
                  <div className="mt-0.5 break-all">{schemaCheck.error}</div>
                  <div className="mt-1 text-red-600">Corrigez le JSON ci-dessus ou cliquez « Régénérer ». Le bouton Appliquer ne s'activera qu'avec un JSON-LD syntaxiquement valide (@context + @type). Rien ne sera marqué corrigé tant que le JSON-LD valide n'a pas été publié puis retrouvé dans le HTML public.</div>
                </div>
              )
            )}
            <div className="flex items-center gap-3 mt-1.5">
              <button onClick={generate} disabled={loading} data-testid="fix-regenerate-button"
                className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline">
                <RefreshCw className="w-3 h-3" /> Régénérer
              </button>
              {!(isSchema && schemaCheck && !schemaCheck.valid) && (
                <button onClick={() => { navigator.clipboard.writeText(suggestion); toast.success("Copié !"); }}
                  data-testid="fix-copy-button" className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
                  <Copy className="w-3 h-3" /> Copier
                </button>
              )}
            </div>
          </div>
        ) : null}

        {source && source !== "github" && !result && !keep && (
          <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="fix-manual-note">
            {source === "wix"
              ? (["Canonical", "Open Graph", "Schema"].includes(issue.category)
                ? "Post de blog Wix : l'écriture automatique Canonical/OG/Schema n'est pas prouvée fiable via l'API Blog — application manuelle (Copier)."
                : "Post de blog Wix : title/meta appliqués automatiquement via l'API Wix. Les pages statiques Wix nécessitent le pont Velo (Phase 3).")
              : source === "emergent_bridge"
                ? "Pont Emergent : correction servie instantanément via le script installé sur le site (Titre, Méta, Canonical, Open Graph, Schema) — sans redéploiement."
                : source === "wix_static" && ["Canonical", "Open Graph", "Schema"].includes(issue.category)
                ? "Page statique Wix : écriture réelle via le pont Velo v2 (script v2 requis — voir panneau Pont Velo, onglet Corrections)."
                : source === "wix_static"
                  ? "Page statique Wix : écriture via le pont Velo (title/meta)."
                  : "Source non modifiable automatiquement — correction à appliquer manuellement (utilisez Copier)."}
          </div>
        )}

        {result?.status === "conflict" && (
          <div className="mt-4 text-sm rounded-md px-3 py-2 border bg-orange-50 border-orange-200 text-orange-800" data-testid="fix-conflict">
            ⚠️ {result.note}
            <div className="mt-2 text-xs">
              <span className="font-semibold">Valeur actuellement en ligne :</span>{" "}
              <span className="italic">{result.current_value || "(vide)"}</span>
            </div>
            <button onClick={() => apply(true)} disabled={applying} data-testid="fix-force-button"
              className="block mt-2 text-xs font-medium text-orange-900 underline">
              Forcer l'application malgré tout
            </button>
          </div>
        )}

        {result && result.status !== "conflict" && (
          <div className={`mt-4 text-sm rounded-md px-3 py-2 border ${["verified", "published"].includes(result.status) ? "bg-green-50 border-green-200 text-green-800" : ["velo_pushed", "bridge_pushed"].includes(result.status) ? "bg-blue-50 border-blue-200 text-blue-800" : result.status === "rejected" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="fix-result">
            {result.status === "verified"
              ? <>✅ Vérifié ✓ — la correction est visible publiquement.</>
              : result.status === "published"
                ? <>✅ Publié {result.commit_sha && <>— commit <code className="font-mono text-xs">{result.commit_sha.slice(0, 7)}</code></>}. {result.note}</>
                : result.status === "velo_pushed"
                  ? <>🔌 Pont Velo — {result.note}</>
                  : result.status === "bridge_pushed"
                    ? <>🔌 Pont Emergent — override enregistré. Diagnostic complet ci-dessous.</>
                    : result.status === "rejected"
                      ? <>⛔ {result.note} Rien n'a été écrit — le problème reste ouvert. Corrigez ou régénérez.</>
                      : <>📋 {result.note}</>}
            {["published", "velo_pushed"].includes(result.status) && result.fix_id && (
              <div className="mt-2 space-y-1">
                {verifyState && !verifyState.gaveUp && (
                  <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="fix-auto-verify-status">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Vérification automatique en cours… (essai {verifyState.attempt}/{verifyState.max})
                  </div>
                )}
                {verifyState?.gaveUp && (
                  <div className="text-xs text-amber-700" data-testid="fix-auto-verify-status">
                    Pas encore visible publiquement (propagation en cours). Revérifiez manuellement dans quelques minutes.
                  </div>
                )}
                <button onClick={verifyNow} data-testid="fix-verify-button"
                  className="block text-xs font-medium text-[#002FA7] underline">
                  Vérifier maintenant sur la page publique
                </button>
              </div>
            )}
          </div>
        )}

        {result?.status === "bridge_pushed" && result.fix_id && (
          <BridgeDiagnosticPanel fixId={result.fix_id} />
        )}

        <div className="mt-6 flex gap-2">
          <button onClick={() => apply(false)} disabled={applying || loading || !!keep || !canApply || ["published", "verified", "velo_pushed"].includes(result?.status || "")}
            data-testid="fix-apply-button"
            className="flex-1 bg-[#002FA7] hover:bg-[#001D6B] disabled:opacity-50 text-white rounded-md py-2 text-sm font-medium">
            {applying ? "Application…" : "Appliquer la correction"}
          </button>
          <button onClick={onClose} className="border border-slate-300 rounded-md px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
            {result ? "Fermer" : "Annuler"}
          </button>
        </div>
      </div>
    </div>
  );
}
